/**
 * LiteracyHeatmapTile — super-admin dashboard card surfacing which
 * abstract metrics customers click `(i)` on most. Powered by
 * /api/analytics/glossary/heatmap.
 *
 * Each row shows: rank · topic title · click count · unique users.
 * When the list is empty (no telemetry yet) we render a friendly
 * priming message rather than an error so the tile doesn't look
 * broken on a fresh tenant.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Lightbulb, TrendingUp, Users } from 'lucide-react';
import { useTelemetryTile } from '../../hooks';

const WINDOW_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
];

export default function LiteracyHeatmapTile() {
  const [windowDays, setWindowDays] = useState(14);
  const { data, loading, error } = useTelemetryTile({
    url: `/analytics/glossary/heatmap?window_days=${windowDays}`,
    deps: [windowDays],
  });

  if (error === 'forbidden') return null;

  const topics = (data?.top_topics || []).slice(0, 5);
  const totals = data?.totals || { events: 0, opens: 0, topic_clicks: 0 };

  return (
    <Card data-testid="literacy-heatmap-tile" className="border-indigo-200 bg-gradient-to-br from-white to-indigo-50/40">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-indigo-500" />
              Literacy Heatmap
            </CardTitle>
            <CardDescription className="mt-0.5">
              Which metrics customers click <kbd className="bg-slate-100 rounded px-1 py-0.5 text-[10px] font-mono">?</kbd> on most — a leading indicator of confusion, useful for content investment.
            </CardDescription>
          </div>
          <div className="inline-flex bg-white border border-slate-200 rounded-md p-0.5" data-testid="literacy-heatmap-window-selector">
            {WINDOW_OPTIONS.map((o) => (
              <button
                key={o.days}
                type="button"
                onClick={() => setWindowDays(o.days)}
                className={
                  'px-2 py-0.5 text-[11px] font-medium rounded transition-colors ' +
                  (windowDays === o.days
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100')
                }
                data-testid={`literacy-heatmap-window-${o.days}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-slate-500" data-testid="literacy-heatmap-loading">Loading…</p>
        ) : error ? (
          <p className="text-xs text-rose-600" data-testid="literacy-heatmap-error">
            Couldn't load heatmap data.
          </p>
        ) : topics.length === 0 ? (
          <div className="text-xs text-slate-500 leading-relaxed" data-testid="literacy-heatmap-empty">
            No <kbd className="bg-slate-100 rounded px-1 font-mono">?</kbd> activity in the last {windowDays} days yet.
            Once customers start using the in-app guide, the most-clicked topics will surface here.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Guide opens</p>
                <p className="text-lg font-bold text-indigo-700 tabular-nums">{totals.opens}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Topic clicks</p>
                <p className="text-lg font-bold text-indigo-700 tabular-nums">{totals.topic_clicks}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Total events</p>
                <p className="text-lg font-bold text-indigo-700 tabular-nums">{totals.events}</p>
              </div>
            </div>
            <ol className="divide-y divide-slate-100" data-testid="literacy-heatmap-list">
              {topics.map((t, i) => (
                <li
                  key={t.topic_testid}
                  className="flex items-center gap-3 py-2 px-1"
                  data-testid={`literacy-heatmap-row-${i}`}
                >
                  <span className="text-[10px] font-mono text-slate-400 w-5">#{i + 1}</span>
                  <span className="flex-1 text-xs text-slate-800 truncate" title={t.topic_testid}>
                    {t.topic_title || t.topic_testid}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 tabular-nums">
                    <TrendingUp className="h-3 w-3" />
                    {t.clicks}
                  </span>
                  <span
                    className="inline-flex items-center gap-1 text-[11px] text-slate-500 tabular-nums"
                    title={`${t.unique_users} unique user${t.unique_users === 1 ? '' : 's'} clicked this topic`}
                  >
                    <Users className="h-3 w-3" />
                    {t.unique_users}
                  </span>
                </li>
              ))}
            </ol>
          </>
        )}
      </CardContent>
    </Card>
  );
}
